// Client helpers for the PR44 email+password auth flow. Cookies are httpOnly
// (set server-side); the SPA is served same-origin so they ride along on fetch.

export type LoginResult =
  | { ok: true; tenant: { slug: string; display_name: string }; role: string }
  | { ok: false; error: string; tenants?: { slug: string; display_name: string }[] };

/** POST /api/auth/login. Returns the tenant/role on success, or a typed error. */
export async function login(email: string, password: string, slug?: string): Promise<LoginResult> {
  const res = await fetch("/api/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password, slug: slug || undefined }),
  });
  const body = await res.json().catch(() => ({}));
  if (res.ok) {
    return { ok: true, tenant: body.tenant, role: body.role };
  }
  return { ok: false, error: body.error || "login_failed", tenants: body.tenants };
}

/** POST /api/auth/logout — clears both session cookies. */
export async function logout(): Promise<void> {
  try {
    await fetch("/api/auth/logout", { method: "POST" });
  } catch {}
}

/** POST /api/auth/password-reset/request — always resolves (no enumeration). */
export async function requestPasswordReset(email: string): Promise<void> {
  await fetch("/api/auth/password-reset/request", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
}

/** POST /api/auth/password-reset/confirm — true on 204. */
export async function confirmPasswordReset(token: string, newPassword: string): Promise<boolean> {
  const res = await fetch("/api/auth/password-reset/confirm", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, new_password: newPassword }),
  });
  return res.status === 204;
}
